import type { ReactNode } from 'react'
import { useEffect, useRef } from 'react'
import type { Thanks } from '../lib/two-step-payment'
import { DefinitionList } from './DefinitionList'
import './payment-thanks.css'

/**
 * A paid payment, thanked in the place the form was.
 *
 * The page does not move: the donor is where they gave, and what they gave is
 * read back to them there — the thanks, the blessing, whether a receipt is
 * coming, and a short record of what was paid. Where to go next is the page's
 * to say, in `children`.
 *
 * Where the column has room, the thanks and the way on take one side and the
 * record the other, so what was paid is read as a record kept beside the
 * thanks rather than more of it.
 */
export function PaymentThanks({
  thanks,
  note,
  appearance = 'form',
  children,
}: {
  thanks: Thanks
  /** A request the payer made, read back as recorded. */
  note?: string
  /** `page` where the thanks IS the page, so its headline is the page's title. */
  appearance?: 'form' | 'page'
  children?: ReactNode
}) {
  /* It replaces the step that had focus, so it takes focus itself and is
     read out, rather than leaving the keyboard at the top of the document. */
  const ref = useRef<HTMLElement>(null)
  useEffect(() => {
    ref.current?.focus()
  }, [])
  return (
    <section
      ref={ref}
      className="payment-thanks"
      aria-label="Thank you"
      tabIndex={-1}
    >
      <div className="payment-thanks__words">
        {appearance === 'page' ? (
          <h1 className="payment-thanks__headline">
            <Figures text={thanks.headline} />
          </h1>
        ) : (
          <p className="payment-thanks__headline">
            <Figures text={thanks.headline} />
          </p>
        )}
        <p className="payment-thanks__blessing">Tizku l’mitzvos!</p>
        <p className="payment-thanks__receipt">{thanks.receipt}</p>
        {note ? <p className="payment-thanks__receipt">{note}</p> : null}
      </div>
      {thanks.record.length > 0 && (
        <div className="payment-thanks__record">
          <DefinitionList
            items={thanks.record.map((row) => ({
              label: row.label,
              details: [row.value],
            }))}
          />
        </div>
      )}
      {children ? <div className="payment-thanks__next">{children}</div> : null}
    </section>
  )
}

/** A sum as the app writes one: a dollar sign, digits and their grouping, and
 *  cents where there are any. */
const SUM = /\$[\d,]+(?:\.\d{2})?/g

/**
 * A sentence with its sums set in the figure face. The headline states what was
 * paid inside a line of prose, and a sum is set in the figure face wherever it
 * appears, including in a sentence set in the serif. Each piece is keyed by
 * where it starts in the sentence, which is what it is.
 */
function Figures({ text }: { text: string }) {
  const pieces: ReactNode[] = []
  let from = 0
  for (const match of text.matchAll(SUM)) {
    const at = match.index
    if (at > from) pieces.push(text.slice(from, at))
    pieces.push(
      <span className="payment-thanks__figure" key={at}>
        {match[0]}
      </span>,
    )
    from = at + match[0].length
  }
  if (from < text.length) pieces.push(text.slice(from))
  return <>{pieces}</>
}
